import React from "react";
import { Badge } from "../ui/badge";
import { ChecklistValidationReport, isChecklistValidationBlocking } from "../../lib/api";

/**
 * Per-checklist report for an aborted multi-checklist migration. Each card lists ONLY the items
 * that checklist itself references — a checklist that validated cleanly is shown as skipped
 * because the batch aborted, never blamed for a sibling's missing objects.
 */
interface Props {
  reports: ChecklistValidationReport[];
  /** Extra context line, e.g. when only the standard subset of a mixed run was aborted. */
  contextNote?: string;
}

const ChecklistValidationReportPanel: React.FC<Props> = ({ reports, contextNote }) => {
  if (!reports.length) {
    return null;
  }
  const blockingCount = reports.filter(isChecklistValidationBlocking).length;

  return (
    <div className="mt-6 rounded-md border border-red-200 bg-red-50 p-4">
      <h3 className="text-lg font-semibold text-red-900">
        Migration aborted — {blockingCount} of {reports.length} checklist(s) failed validation
      </h3>
      <p className="text-sm text-red-800 mt-1">
        Nothing was migrated. Resolve each checklist's missing items below, then retry. Missing
        object types, properties and relations can be migrated via the Ontology Migration flow;
        SYSTEM properties and missing objects must be created on the target.
      </p>
      {contextNote && <p className="text-sm text-red-800 mt-1">{contextNote}</p>}

      <div className="mt-4 space-y-4">
        {reports.map((report) => {
          const blocking = isChecklistValidationBlocking(report);
          return (
            <div
              key={report.checklistId}
              className="rounded-md border border-red-200 bg-white p-3"
            >
              <div className="flex items-center gap-2">
                <span className="font-medium">{report.checklistName}</span>
                <span className="text-xs text-muted-foreground">id {report.checklistId}</span>
                <Badge
                  variant={blocking ? "destructive" : "outline"}
                  className={
                    blocking
                      ? "bg-red-100 text-red-800 hover:bg-red-100 border-red-200"
                      : "bg-amber-50 text-amber-900 hover:bg-amber-50 border-amber-200"
                  }
                >
                  {blocking ? "Validation failed" : "Skipped — batch aborted"}
                </Badge>
              </div>

              {blocking ? (
                <ul className="mt-2 space-y-1 text-sm text-red-900 list-disc list-inside">
                  {report.missingObjectTypes.map((r) => (
                    <li key={`ot-${r.id}`}>
                      Missing object type on target:{" "}
                      <span className="font-medium">{r.displayName || r.externalId || r.id}</span>
                    </li>
                  ))}
                  {report.missingDependencies.map((d, i) => (
                    <li key={`dep-${i}`}>
                      Missing {d.kind === "RELATION" ? "relation" : d.systemProperty ? "SYSTEM property" : "property"} on
                      target:{" "}
                      <span className="font-medium">
                        {d.objectTypeName ? `${d.objectTypeName}.` : ""}
                        {d.displayName || d.externalId || d.entityId}
                      </span>
                    </li>
                  ))}
                  {report.missingObjects.map((m) => (
                    <li key={`obj-${m.sourceObjectId}`}>
                      Missing object on target:{" "}
                      <span className="font-medium">{m.sourceExternalId || m.sourceObjectId}</span>
                      {m.objectTypeExternalId ? (
                        <span className="text-muted-foreground"> ({m.objectTypeExternalId})</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-sm text-muted-foreground">
                  This checklist validated cleanly — it was skipped only because the batch aborted.
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ChecklistValidationReportPanel;
